import type { GameEvent, GamePhase } from "../game/types";

type Tone = {
  hz: number; endHz?: number; duration: number; volume: number;
  delay?: number; attack?: number; type?: OscillatorType; pan?: number; music?: boolean;
};
type Noise = {
  duration: number; volume: number; frequency: number; endFrequency?: number;
  delay?: number; attack?: number; type?: BiquadFilterType; pan?: number; music?: boolean;
};

const midiHz = (note: number): number => 440 * 2 ** ((note - 69) / 12);

export class AudioManager {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private effectsBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private readonly attachedWindows = new WeakSet<Window>();
  private readonly voices = new Set<AudioScheduledSourceNode>();
  private readonly musicVoices = new Set<AudioScheduledSourceNode>();
  private muted = false;
  private volume = 0.65;
  private musicEnabled = true;
  private musicPlaying = false;
  private nextBeat = 0;
  private beat = 0;
  private readonly lastCue = new Map<string, number>();

  constructor(private readonly onStatus: (message: string) => void = () => {}) {
    this.attachWindow(window);
  }

  attachWindow(target: Window): void {
    if (this.attachedWindows.has(target)) return;
    this.attachedWindows.add(target);
    // The Go popup and the desktop share one audio engine. Either window's
    // user gesture can unlock playback; no duplicate soundtrack is created.
    target.addEventListener("keydown", this.unlock);
    target.addEventListener("pointerdown", this.unlock);
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyVolume();
    if (muted) this.stopMusic();
    this.reportStatus();
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    this.applyVolume();
    if (this.volume === 0) this.stopMusic();
    this.reportStatus();
  }

  setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    if (!enabled) this.stopMusic();
  }

  update(phase: GamePhase, level: number): void {
    const context = this.context;
    if (!context || context.state !== "running" || phase !== "playing" || !this.musicEnabled || this.muted || this.volume === 0) {
      this.stopMusic();
      return;
    }
    const now = context.currentTime;
    if (!this.musicPlaying) {
      this.musicPlaying = true;
      this.nextBeat = now + 0.035;
      this.beat = 0;
      this.musicBus!.gain.setTargetAtTime(0.45, now, 0.03);
    }
    // Schedule against the audio clock with a short lookahead. Long render
    // stalls skip missed beats instead of playing a burst of overdue notes.
    const interval = 60 / (108 + Math.min(8, level - 1) * 2) / 4;
    if (this.nextBeat < now) this.nextBeat = now + 0.035;
    while (this.nextBeat < now + 0.18) {
      const delay = Math.max(0, this.nextBeat - now);
      const step = this.beat % 16;
      const chord = [45, 41, 48, 43][Math.floor(this.beat / 16) % 4]!;
      if (step % 4 === 0) {
        this.tone({ hz: 125, endHz: 42, duration: 0.18, volume: 0.095, type: "sine", delay, music: true });
        this.tone({ hz: midiHz(chord - 12), duration: interval * 2.6, volume: 0.065, type: "triangle", delay, attack: 0.012, music: true });
      }
      if (step % 2 === 1) this.noise({ duration: 0.045, volume: 0.018, frequency: 7500, type: "highpass", delay, music: true });
      if (step === 4 || step === 12) this.noise({ duration: 0.12, volume: 0.035, frequency: 1800, type: "bandpass", delay, music: true });
      if (step % 2 === 0) {
        const offset = [0, 7, 12, 15, 19, 15, 12, 7][step / 2]!;
        this.tone({ hz: midiHz(chord + 12 + offset), duration: interval * 1.8, volume: 0.025, delay, attack: 0.008, pan: step % 4 === 0 ? -0.3 : 0.3, music: true });
      }
      this.beat += 1;
      this.nextBeat += interval;
    }
  }

  preview(): void {
    this.handle([{ type: "line-clear", layer: "front", rows: [12, 13] }]);
  }

  handle(events: readonly GameEvent[]): void {
    if (!this.context || this.muted || this.volume === 0) return;
    for (const event of events) {
      const pan = "piece" in event ? (event.piece.layer === "front" ? -0.18 : 0.18) : 0;
      switch (event.type) {
        case "start":
          this.stopVoices();
          this.lastCue.clear();
          [57, 64, 69, 76].forEach((note, i) => this.tone({ hz: midiHz(note), duration: 0.22, volume: 0.075, delay: i * 0.075, type: "triangle" }));
          break;
        case "move":
          if (this.allowCue("move", 0.045)) this.tone({ hz: 320, endHz: 210, duration: 0.032, volume: 0.03, type: "triangle", pan });
          break;
        case "rotate":
          this.tone({ hz: 440, endHz: 880, duration: 0.075, volume: 0.045, type: "triangle", pan });
          this.tone({ hz: 1320, duration: 0.065, volume: 0.02, delay: 0.025, pan: -pan });
          break;
        case "drop":
          if (event.hard) {
            this.noise({ duration: 0.2, volume: 0.12, frequency: 4200, endFrequency: 200, pan });
            this.tone({ hz: 550, endHz: 55, duration: 0.16, volume: 0.085, type: "triangle", pan });
          } else if (this.allowCue("soft-drop", 0.09)) {
            this.tone({ hz: 160, endHz: 120, duration: 0.027, volume: 0.025, pan });
          }
          break;
        case "lock":
          this.tone({ hz: 100, endHz: 42, duration: 0.2, volume: 0.13, pan });
          this.noise({ duration: 0.075, volume: 0.07, frequency: 1600, endFrequency: 400, type: "lowpass", pan });
          this.tone({ hz: 1240, endHz: 920, duration: 0.075, volume: 0.025, pan });
          break;
        case "layer-switch": {
          const direction = event.to === "front" ? 1 : -1;
          // A short preload, then the transfer and a small latch on arrival.
          this.tone({ hz: 95, endHz: 60, duration: 0.025, volume: 0.055, type: "triangle" });
          this.tone({ hz: direction > 0 ? 330 : 990, endHz: direction > 0 ? 990 : 330, duration: 0.075, delay: 0.025, volume: 0.06, pan: direction * -0.35 });
          this.noise({ duration: 0.08, delay: 0.02, volume: 0.055, frequency: 1600, endFrequency: direction > 0 ? 5500 : 400, pan: direction * 0.35 });
          this.tone({ hz: 1100, endHz: 800, duration: 0.035, delay: 0.085, volume: 0.025, pan: direction * -0.2 });
          break;
        }
        case "layer-blocked":
          if (this.allowCue("blocked", 0.12)) [0, 0.09].forEach(delay => this.tone({ hz: 110, endHz: 85, duration: 0.075, volume: 0.05, type: "triangle", delay }));
          break;
        case "line-clear": {
          const lines = Math.max(1, Math.min(4, event.rows.length));
          const side = event.layer === "front" ? -0.25 : 0.25;
          this.noise({ duration: 1.8 + lines * 0.14, volume: 0.14, frequency: 260, endFrequency: 6500, attack: 0.14, pan: side });
          this.tone({ hz: 65, endHz: 38, duration: 0.5, volume: 0.16 });
          [69, 76, 81, 88].slice(0, lines).forEach((note, i) => {
            this.tone({ hz: midiHz(note), endHz: midiHz(note + 12), duration: 0.48, volume: 0.065, attack: 0.018, delay: i * 0.1, pan: i % 2 === 0 ? side : -side });
          });
          break;
        }
        case "level-up":
          [69, 73, 76, 81].forEach((note, i) => this.tone({ hz: midiHz(note), duration: 0.35, volume: 0.065, delay: i * 0.13, type: "triangle" }));
          break;
        case "game-over":
          this.stopMusic();
          this.noise({ duration: 6, volume: 0.17, frequency: 2400, endFrequency: 60, attack: 0.1, type: "lowpass" });
          this.tone({ hz: 85, endHz: 25, duration: 5.8, volume: 0.13, attack: 0.04 });
          [57, 52, 48, 45, 40, 33].forEach((note, i) => this.tone({ hz: midiHz(note), endHz: midiHz(note - 2), duration: 0.65, volume: 0.065, delay: i * 0.72, type: "triangle", pan: i % 2 ? 0.2 : -0.2 }));
          break;
        case "pause":
          if (event.paused) this.stopMusic();
          this.tone({ hz: event.paused ? 440 : 330, endHz: event.paused ? 220 : 660, duration: 0.15, volume: 0.045 });
          break;
      }
    }
  }

  private readonly unlock = (): void => {
    try {
      if (!this.context) this.initialize();
      const context = this.context!;
      void context.resume().then(() => this.reportStatus()).catch(() => this.onStatus("音声を開始できませんでした。もう一度操作してください"));
    } catch {
      this.onStatus("このブラウザーではサウンドを利用できません");
    }
  };

  private initialize(): void {
    const context = new AudioContext({ latencyHint: "interactive" });
    this.context = context;
    this.master = context.createGain();
    this.effectsBus = context.createGain();
    this.musicBus = context.createGain();
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -14;
    compressor.knee.value = 12;
    compressor.ratio.value = 8;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.15;
    this.effectsBus.connect(compressor);
    this.musicBus.connect(compressor);
    compressor.connect(this.master).connect(context.destination);
    this.applyVolume();
    // Short, shared reverb and reusable filtered noise keep the effects rich
    // without loading assets or allocating buffers for every repeated input.
    const reverb = context.createConvolver();
    const impulse = context.createBuffer(2, Math.ceil(context.sampleRate * 0.28), context.sampleRate);
    for (let channel = 0; channel < 2; channel += 1) {
      const samples = impulse.getChannelData(channel);
      for (let i = 0; i < samples.length; i += 1) samples[i] = (Math.random() * 2 - 1) * (1 - i / samples.length) ** 3;
    }
    reverb.buffer = impulse;
    const wet = context.createGain();
    wet.gain.value = 0.12;
    this.effectsBus.connect(reverb).connect(wet).connect(compressor);
    this.musicBus.connect(reverb);
    this.noiseBuffer = context.createBuffer(1, context.sampleRate, context.sampleRate);
    const noise = this.noiseBuffer.getChannelData(0);
    for (let i = 0; i < noise.length; i += 1) noise[i] = Math.random() * 2 - 1;
    context.onstatechange = () => this.reportStatus();
  }

  private reportStatus(): void {
    this.onStatus(this.muted || this.volume === 0 ? "ミュート中" : this.context?.state === "running" ? "サウンド再生可能 · Spaceで開始" : "最初の操作で音声を開始");
  }

  private applyVolume(): void {
    if (!this.context || !this.master) return;
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.volume, this.context.currentTime, 0.015);
  }

  private allowCue(name: string, interval: number): boolean {
    const now = this.context!.currentTime;
    if (now - (this.lastCue.get(name) ?? -Infinity) < interval) return false;
    this.lastCue.set(name, now);
    return true;
  }

  private tone(options: Tone): void {
    if (!this.context || this.voices.size >= 96) return;
    const oscillator = this.context.createOscillator();
    const now = this.context.currentTime + (options.delay ?? 0);
    oscillator.type = options.type ?? "sine";
    oscillator.frequency.setValueAtTime(options.hz, now);
    if (options.endHz) oscillator.frequency.exponentialRampToValueAtTime(options.endHz, now + options.duration);
    this.play(oscillator, options, now);
  }

  private noise(options: Noise): void {
    if (!this.context || !this.noiseBuffer || this.voices.size >= 96) return;
    const source = this.context.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    const now = this.context.currentTime + (options.delay ?? 0);
    const filter = this.context.createBiquadFilter();
    filter.type = options.type ?? "bandpass";
    filter.Q.value = 0.7;
    filter.frequency.setValueAtTime(options.frequency, now);
    if (options.endFrequency) filter.frequency.exponentialRampToValueAtTime(options.endFrequency, now + options.duration);
    source.connect(filter);
    this.play(source, options, now, filter);
  }

  private play(source: AudioScheduledSourceNode, options: Tone | Noise, now: number, signal: AudioNode = source): void {
    const context = this.context!;
    const gain = context.createGain();
    const panner = context.createStereoPanner();
    const attack = options.attack ?? 0.003;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(options.volume, now + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + options.duration);
    panner.pan.value = options.pan ?? 0;
    signal.connect(gain).connect(panner).connect(options.music ? this.musicBus! : this.effectsBus!);
    this.voices.add(source);
    if (options.music) this.musicVoices.add(source);
    source.onended = () => {
      this.voices.delete(source);
      this.musicVoices.delete(source);
      source.disconnect();
      if (signal !== source) signal.disconnect();
      gain.disconnect();
      panner.disconnect();
    };
    source.start(now);
    source.stop(now + options.duration + 0.02);
  }

  private stopMusic(): void {
    if (!this.musicPlaying) return;
    this.musicPlaying = false;
    this.musicBus!.gain.setTargetAtTime(0, this.context!.currentTime, 0.01);
    for (const voice of this.musicVoices) voice.stop(this.context!.currentTime + 0.025);
    this.musicVoices.clear();
  }

  private stopVoices(): void {
    this.stopMusic();
    for (const voice of this.voices) voice.stop(this.context!.currentTime);
    this.voices.clear();
  }
}
